/**
 * useGenerationMutation
 *
 * Wraps the image generation mutation with toast feedback,
 * confetti, and query invalidation. Max 50 lines.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { generateImageV2 } from '@/lib/api/generation';
import { triggerConfetti } from './confetti';

interface GenerationMutationOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

export function useGenerationMutation({ onSuccess, onError }: GenerationMutationOptions) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: generateImageV2,
    onSuccess: (response) => {
      toast.success(response.message || t('toast.success.imageGenerated'));
      triggerConfetti();
      queryClient.invalidateQueries({ queryKey: ['workspace', 'images'] });
      queryClient.invalidateQueries({ queryKey: ['workspace', 'config'] });
      queryClient.invalidateQueries({ queryKey: ['gallery-v2', 'search'] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.error.generating'));
      onError?.();
    },
  });

  return mutation;
}
